import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
const easeOut = [0.16, 1, 0.3, 1] as const;
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const trust = [{
    value: '100+',
    label: lang === 'es' ? 'Negocios impulsados' : 'Businesses launched',
    labelShort: lang === 'es' ? 'Negocios' : 'Businesses'
  }, {
    value: '100%',
    label: lang === 'es' ? 'Estrategia personalizada' : 'Personalized strategy',
    labelShort: lang === 'es' ? 'Personalizado' : 'Personalized'
  }, {
    value: '< 2 sem',
    valueEn: '< 2 wks',
    label: lang === 'es' ? 'Entrega promedio' : 'Average delivery',
    labelShort: lang === 'es' ? 'Entrega prom.' : 'Avg. delivery'
  }];
  const fadeUp = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6,
      ease: easeOut,
      delay
    }
  });
  const fade = (delay: number, duration = 0.6) => ({
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    transition: {
      duration,
      ease: easeOut,
      delay
    }
  });
  return <section id="inicio" className="relative w-full lg:min-h-[90vh] flex flex-col lg:flex-row overflow-hidden bg-[linear-gradient(to_bottom,#FAF8F5_60%,#EDE9FF_100%)] lg:bg-[#FAF8F5] lg:bg-none" data-id="element-209">
      {/* ─────────────── TEXT BLOCK ─────────────── */}
      <div className="relative z-10 w-full lg:w-[55%] flex flex-col justify-center px-6 pt-12 pb-6 lg:py-0 lg:pl-[max(6vw,calc((100vw-1320px)/2+24px))] lg:pr-12" data-id="element-210">
        {/* Eyebrow */}
        <motion.div {...fade(0.1)} className="flex items-center mb-3 lg:mb-6" data-id="element-211">
          <span className="inline-block w-[24px] h-[1.5px] bg-[#5A4579] mr-3 lg:w-[28px] lg:h-[2px] lg:bg-[#A78BCA]" data-id="element-212" />
          <span className="font-sans text-[10px] font-bold tracking-[3px] uppercase text-[#5A4579] lg:text-[11px] lg:font-semibold" data-id="element-213">
            {lang === 'es' ? 'Miami · Consultoría Digital' : 'Miami · Digital Consulting'}
          </span>
        </motion.div>

        {/* Headline — focal point */}
        <h1 className="font-display font-bold text-[#0F0A1E] text-[34px] leading-[1.18] sm:text-[40px] lg:text-[58px] lg:leading-[1.08] tracking-[-0.5px] lg:tracking-[-1.5px] mb-5 lg:mb-6" data-id="element-214">
          {lang === 'es' ? <>
              <motion.span {...fadeUp(0.2)} className="block" data-id="element-215">
                Tu negocio digital,
              </motion.span>
              <motion.span {...fadeUp(0.32)} className="block" data-id="element-216">
                construido desde tu
              </motion.span>
              <motion.span {...fadeUp(0.44)} className="block italic font-semibold text-[#A78BCA]" data-id="element-217">
                identidad y propósito.
              </motion.span>
            </> : <>
              <motion.span {...fadeUp(0.2)} className="block" data-id="element-218">
                Your digital business,
              </motion.span>
              <motion.span {...fadeUp(0.32)} className="block" data-id="element-219">
                built from your
              </motion.span>
              <motion.span {...fadeUp(0.44)} className="block italic font-semibold text-[#A78BCA]" data-id="element-220">
                identity and purpose.
              </motion.span>
            </>}
        </h1>

        {/* Supporting text — short and refined */}
        <motion.p {...fade(0.56)} style={{
        backgroundColor: 'transparent',
        WebkitTextFillColor: '#5a5368'
      }} className="font-sans text-[15px] lg:text-[17px] leading-[1.65] text-[#5a5368] max-w-[480px] mb-9 lg:mb-10 bg-transparent [&_*]:bg-transparent" data-id="element-221">
          {lang === 'es' ? 'Diseñamos una presencia digital clara, estratégica y alineada con el valor real de tu negocio.' : 'We design a clear, strategic digital presence aligned with the real value of your business.'}
        </motion.p>

        {/* CTA hierarchy */}
        <motion.div {...fade(0.68)} className="flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6 mb-10 lg:mb-14" data-id="element-222">
          {/* Primary CTA + microcopy */}
          <div className="flex flex-col items-center lg:items-start w-full lg:w-auto" data-id="element-223">
            <a href="https://calendly.com/inkovacreation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-gradient-to-r from-[#5A4579] to-[#5A4579] text-white font-sans font-semibold text-[15px] lg:text-[13px] tracking-[0.6px] uppercase h-[54px] lg:h-auto px-10 lg:py-4 rounded-[50px] transition-all duration-200 ease-in-out hover:from-[#3D2F5A] hover:to-[#3D2F5A] hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(90,69,121,0.3)] w-full lg:w-auto" data-id="element-224">
              {lang === 'es' ? 'Da el primer paso' : 'Take the first step'}
            </a>
            <span className="font-sans text-[11px] tracking-[0.4px] text-[#9b8aaa] mt-3 text-center lg:text-left" data-id="element-225">
              {lang === 'es' ? 'Sesión diagnóstica · 30 min · USD 50' : 'Diagnostic session · 30 min · USD 50'}
            </span>
          </div>

          {/* Secondary CTA — outlined purple pill on desktop, text-only on mobile */}
          <a href="https://wa.me/13054781618" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 font-sans text-[14px] text-[#5A4579] transition-all duration-200 ease-in-out lg:border-[1.5px] lg:border-solid lg:border-[#5A4579] lg:rounded-[100px] lg:px-6 lg:py-3 lg:bg-transparent lg:hover:bg-[#F0EDFB]" data-id="element-226">
            <span data-id="element-227">
              {lang === 'es' ? 'Tengo una pregunta' : 'I have a question'}
            </span>
            <span className="transition-transform duration-200 ease-in-out group-hover:translate-x-[3px]" data-id="element-228">
              → WhatsApp
            </span>
          </a>
        </motion.div>

        {/* ─────────────── STATS BLOCK ─────────────── */}
        <motion.div {...fade(0.8)} className="flex items-center justify-center gap-2 w-full lg:justify-start lg:gap-6" data-id="element-229">
          {trust.map((item, index) => <Fragment key={index} data-id="element-230">
              <div className="flex flex-1 flex-col items-center text-center lg:flex-none lg:items-start lg:text-left" data-id="element-231">
                <span className="font-display font-bold text-[24px] lg:text-[34px] text-[#0F0A1E] leading-none" data-id="element-232">
                  {lang === 'en' && item.valueEn ? item.valueEn : item.value}
                </span>
                <span className="font-sans text-[9px] tracking-[0.5px] text-[#6B7280] lg:text-[11px] lg:tracking-[0.6px] lg:text-[#9b8aaa] uppercase mt-2 leading-snug whitespace-nowrap" data-id="element-233">
                  <span className="lg:hidden" data-id="element-234">{item.labelShort}</span>
                  <span className="hidden lg:inline" data-id="element-235">{item.label}</span>
                </span>
              </div>
              {index < trust.length - 1 && <div className="w-[1px] h-[32px] lg:h-[40px] bg-[#E5DDF5]" data-id="element-236" />}
            </Fragment>)}
        </motion.div>
      </div>

      {/* ─────────────── PORTRAIT BLOCK ─────────────── */}
      <motion.div {...fade(0, 1.2)} className="relative z-0 w-full h-[280px] mt-0 bg-[#FAF8F5] lg:bg-transparent lg:h-[90vh] lg:w-[45%] lg:absolute lg:top-0 lg:right-0 lg:bottom-0 overflow-hidden" data-id="element-237">
        <img src="https://cdn.magicpatterns.com/uploads/8wA1hfb42Whxpi23trRcAX/anny_png.png" alt="Annysol Navarro, fundadora de Inkova Creation" loading="eager" className="w-full h-full object-cover [object-position:top_center] [mix-blend-mode:multiply] lg:[object-position:center] lg:[mix-blend-mode:normal]" data-id="element-238" />
        {/* Gradient overlay TOP edge on mobile — fades photo into the section above */}
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-[linear-gradient(to_bottom,#FAF8F5_0%,transparent_40%)] lg:hidden" data-id="element-239" />
        {/* Gradient overlay LEFT edge on desktop */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5] to-transparent w-[200px] hidden lg:block" data-id="element-240" />
      </motion.div>
    </section>;
}